import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Video, FileText, TrendingUp, Award, Clock, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateInterviewReport } from '../../services/aiInterviewService';

export default function InterviewReport() {
  const { interviewId, candidateId } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchInterviewReport();
  }, []);

  const fetchInterviewReport = async () => {
    try {
      const docRef = doc(db, 'interviews', interviewId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const interviewData = { id: docSnap.id, ...docSnap.data() };
        setInterview(interviewData);
        
        const candidateData = interviewData.candidates?.find(c => c.id === candidateId);
        if (candidateData) {
          setCandidate(candidateData);
          
          // If no analysis yet, trigger AI analysis
          if (!candidateData.aiAnalysis && candidateData.status === 'completed') {
            performAIAnalysis(candidateData);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching interview report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const performAIAnalysis = async (candidateData) => {
    setAnalyzing(true);
    try {
      console.log('Starting AI analysis for candidate:', candidateData.name);
      
      // Use the real AI service
      console.log('Calling AI service...');
      const aiAnalysis = await generateInterviewReport(
        candidateData,
        candidateData.answers || [],
        interview
      );
      
      console.log('AI analysis received:', aiAnalysis);
      
      // Update candidate with AI analysis
      console.log('Fetching interview document...');
      const interviewRef = doc(db, 'interviews', interviewId);
      const interviewDoc = await getDoc(interviewRef);
      
      if (!interviewDoc.exists()) {
        throw new Error('Interview document not found');
      }
      
      const interviewData = interviewDoc.data();
      console.log('Interview data fetched, updating candidates...');
      
      const updatedCandidates = interviewData.candidates.map(c => {
        if (c.id === candidateId) {
          return {
            ...c,
            aiAnalysis,
            overallRating: aiAnalysis.overallRating,
            analyzedAt: new Date().toISOString()
          };
        }
        return c;
      });

      console.log('Updating Firestore...');
      await updateDoc(interviewRef, {
        candidates: updatedCandidates
      });

      console.log('Firestore updated successfully');
      setCandidate(prev => ({
        ...prev,
        aiAnalysis,
        overallRating: aiAnalysis.overallRating
      }));

      toast.success('AI analysis completed!');
    } catch (error) {
      console.error('Error performing AI analysis:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to analyze interview';
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please ensure you have admin access.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setAnalyzing(false);
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 85) return 'text-green-600 bg-green-100';
    if (rating >= 70) return 'text-blue-600 bg-blue-100';
    if (rating >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getRecommendationColor = (recommendation) => {
    if (recommendation === 'Strongly Recommend') return 'bg-green-100 text-green-800 border-green-300';
    if (recommendation === 'Recommend') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (recommendation === 'Consider') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!interview || !candidate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Report Not Found</h2>
          <button
            onClick={() => navigate('/admin/interview-management')}
            className="text-blue-600 hover:underline"
          >
            Back to Interviews
          </button>
        </div>
      </div>
    );
  }

  const analysis = candidate.aiAnalysis;
  const needsAnalysis = !analysis && candidate.status === 'completed';

  if (needsAnalysis && !analyzing) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/admin/interview-management')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Interviews
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Interview Completed - Analysis Pending
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {candidate.name} has completed the interview. Click below to generate the AI analysis report.
          </p>
          <button
            onClick={() => performAIAnalysis(candidate)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Generate AI Analysis
          </button>
        </div>
      </div>
    );
  }

  if (analyzing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            AI Analysis in Progress
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Analyzing interview responses and generating detailed report...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <button
        onClick={() => navigate('/admin/interview-management')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Interviews
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {candidate.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-1">{candidate.email}</p>
            <p className="text-gray-600 dark:text-gray-400">
              Position: <span className="font-medium">{interview.positionTitle}</span>
            </p>
          </div>
          <div className="text-right">
            <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg ${getRatingColor(analysis?.overallRating || 0)}`}>
              <Award className="w-6 h-6" />
              <div>
                <div className="text-2xl font-bold">{analysis?.overallRating || 0}/100</div>
                <div className="text-xs">Overall Score</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {analysis && (
        <>
          {/* Recommendation */}
          <div className={`border-2 rounded-lg p-6 mb-6 ${getRecommendationColor(analysis.recommendation)}`}>
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-6 h-6" />
              <h2 className="text-xl font-bold">Recommendation: {analysis.recommendation}</h2>
            </div>
            <p className="text-sm">{analysis.summary}</p>
          </div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Technical Skills
              </h3>
              <div className={`text-2xl font-bold ${getRatingColor(analysis.technicalSkills.rating)}`}>
                {analysis.technicalSkills.rating}/100
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                {analysis.technicalSkills.feedback}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Communication
              </h3>
              <div className={`text-2xl font-bold ${getRatingColor(analysis.communication.rating)}`}>
                {analysis.communication.rating}/100
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                {analysis.communication.feedback}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Problem Solving
              </h3>
              <div className={`text-2xl font-bold ${getRatingColor(analysis.problemSolving.rating)}`}>
                {analysis.problemSolving.rating}/100
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                {analysis.problemSolving.feedback}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Culture Fit
              </h3>
              <div className={`text-2xl font-bold ${getRatingColor(analysis.cultureFit.rating)}`}>
                {analysis.cultureFit.rating}/100
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                {analysis.cultureFit.feedback}
              </p>
            </div>
          </div>

          {/* Strengths & Improvements */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-green-600" />
                Strengths
              </h3>
              <ul className="space-y-2">
                {analysis.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                    <span className="text-green-600 mt-1">✓</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Areas for Improvement
              </h3>
              <ul className="space-y-2">
                {analysis.areasForImprovement.map((area, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                    <span className="text-blue-600 mt-1">→</span>
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Question-by-Question Analysis */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Question-by-Question Analysis
            </h3>
            <div className="space-y-6">
              {analysis.questionRatings.map((qRating, index) => (
                <div key={index} className="border-b dark:border-gray-700 pb-6 last:border-b-0">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                        Question {index + 1}
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300 mb-2">{qRating.question}</p>
                    </div>
                    <div className={`ml-4 px-4 py-2 rounded-lg ${getRatingColor(qRating.rating)}`}>
                      <div className="text-xl font-bold">{qRating.rating}/100</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <FileText className="w-4 h-4" />
                      <span className="font-medium">Transcript:</span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">
                      {qRating.transcript}
                    </p>
                  </div>

                  {candidate.answers?.[index]?.videoUrl && (
                    <div className="mb-3">
                      <a
                        href={candidate.answers[index].videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm"
                      >
                        <Video className="w-4 h-4" />
                        View Video Response
                      </a>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h5 className="font-medium text-green-700 dark:text-green-400 mb-1">
                        Strengths:
                      </h5>
                      <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                        {qRating.strengths.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-blue-700 dark:text-blue-400 mb-1">
                        Improvements:
                      </h5>
                      <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                        {qRating.improvements.map((imp, i) => (
                          <li key={i}>{imp}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-2">
                    <Clock className="w-4 h-4" />
                    <span>Time spent: {Math.floor(qRating.timeSpent / 60)}:{(qRating.timeSpent % 60).toString().padStart(2, '0')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-4">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Report
            </button>
            <button
              onClick={() => performAIAnalysis(candidate)}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Re-analyze Interview
            </button>
          </div>
        </>
      )}
    </div>
  );
}
